/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Stack } from '@mui/material';
import React from 'react';
import FilterChip from './FilterChip';
import { FilterToggleButton } from './FilterToggleButton';
import { useSharedTranslation } from '../../utils/i18n';

export interface ListFilter {
  label: string;
  id: string;
  type?: 'scope' | 'keyword';
  isSelected?: boolean;
  isDisabled?: boolean;
}

interface FilterListProps {
  filters?: ListFilter[];
  onChipClick?: (id: string, selected: boolean) => void;
  isAllSelected?: boolean;
  showLabelFilterToggle?: boolean;
  isLabelFilterOpen?: boolean;
  onToggleLabelFilter?: () => void;
  activeLabelFilterCount?: number;
}

const FilterList: React.FC<FilterListProps> = ({
  filters = [],
  onChipClick = (): void => {},
  isAllSelected = false,
  showLabelFilterToggle = false,
  isLabelFilterOpen = false,
  onToggleLabelFilter,
  activeLabelFilterCount = 0,
}: FilterListProps) => {
  const { t } = useSharedTranslation();

  return (
    <Stack
      className="filter-list"
      data-testid="filterList"
      direction="row"
      sx={{
        flexWrap: 'wrap',
        gap: 1,
        rowGap: 1,
        alignItems: 'center',
      }}
    >
      {showLabelFilterToggle && onToggleLabelFilter ? (
        <FilterToggleButton
          activeLabelFilterCount={activeLabelFilterCount}
          isOpen={isLabelFilterOpen}
          onToggle={onToggleLabelFilter}
        />
      ) : null}
      <FilterChip
        handleClick={(key): void => onChipClick(key, !isAllSelected)}
        id="all"
        isSelected={isAllSelected}
        label={t('shared-filter-all')}
      />
      {filters.map((filter) => (
        <FilterChip
          handleClick={(key): void => {
            // If they are all isSelected ensure that if one is clicked that one will be the only one set to selected
            const selected = isAllSelected ? true : !filter.isSelected;
            onChipClick(key, selected);
          }}
          id={filter.id}
          isDisabled={filter.isDisabled}
          isSelected={isAllSelected ? false : filter.isSelected}
          key={filter.id}
          label={t(filter.label)}
        />
      ))}
    </Stack>
  );
};

export default FilterList;
